import { normalizeValue, shouldUseLogScale } from './QuadrantChart/scaleUtils';

import type { StudentQuadrantData } from './types';

// The chart draws its dividing lines at half of each axis. Half itself counts as high: TANAI-2541
// puts "50 or more" on the high side of its fixed quality cut, and both measurements must compare.
const HIGH_SIDE_THRESHOLD = 0.5;

type QuadrantCounts = {
  bad_use_count: number;
  efficient_count: number;
  low_commitment_count: number;
  motivated_count: number;
  students_total: number;
  unscored_count: number;
};

function isUnscored({ intentCounts, totalMessages }: StudentQuadrantData): boolean {
  return totalMessages > 0 && Object.values(intentCounts).every(({ totalScore }) => totalScore === null);
}

function getQuadrantCounts(students: StudentQuadrantData[]): QuadrantCounts {
  // Same maths as useQuadrantPositions, whose own scales cannot be read: they are zero until the
  // chart is measured. Unscored students stay in the scales because the chart draws them too.
  const qualities = students.map(({ qualityScore }) => qualityScore);
  const volumes = students.map(({ totalMessages }) => totalMessages);
  const maxQuality = Math.max(...qualities, 1);
  const maxVolume = Math.max(...volumes, 1);
  const qualityScale = shouldUseLogScale(qualities) ? 'log' : 'linear';
  const volumeScale = shouldUseLogScale(volumes) ? 'log' : 'linear';
  const counts: QuadrantCounts = {
    bad_use_count: 0,
    efficient_count: 0,
    low_commitment_count: 0,
    motivated_count: 0,
    students_total: students.length,
    unscored_count: 0,
  };

  students.forEach((student) => {
    if (isUnscored(student)) {
      counts.unscored_count += 1;
      return;
    }

    const isHighQuality = normalizeValue(student.qualityScore, maxQuality, qualityScale) >= HIGH_SIDE_THRESHOLD;
    const isHighVolume = normalizeValue(student.totalMessages, maxVolume, volumeScale) >= HIGH_SIDE_THRESHOLD;

    if (isHighQuality && isHighVolume) counts.motivated_count += 1;
    else if (isHighQuality) counts.efficient_count += 1;
    else if (isHighVolume) counts.bad_use_count += 1;
    else counts.low_commitment_count += 1;
  });

  return counts;
}

export default getQuadrantCounts;
